'use client';

import { useState } from 'react';
import { type Control, useForm } from 'react-hook-form';
import type { FieldValues } from 'react-hook-form';
import ActionButton from '../../../../components/form/ActionButton/ActionButton';
import FormCheckbox from '../../../../components/form/FormCheckbox/FormCheckbox';
import FormSelect from '../../../../components/form/FormSelect/FormSelect';
import FormText from '../../../../components/form/FormText/FormText';
import FormTextArea from '../../../../components/form/FormTextArea/FormTextArea';
import { ModalBase } from '../../../../components/ui';
import { swal } from '../../../../lib';
import fontsStyles from '../../../../styles/modules/fonts.module.scss';
import layoutStyles from '../../../../styles/modules/layout.module.scss';
import utilsStyles from '../../../../styles/modules/utils.module.scss';
import { WCHANCE_TYPE } from '../../constants';
import type { WChanceSurveyFormItem } from '../../types';
import styles from './SurveyForm.module.scss';
import currentStyles from './SurveyForm.module.scss';

export interface SurveyFormProps {
  id: string;
  hideSubmit?: boolean;
  isPreview?: boolean;
  customForms: WChanceSurveyFormItem[];
  lotteryEventId: number;
  lotteryDrawHistoryId?: number;
  lotteryPrizeLabelId?: number;
  wChanceType?: 'manual' | 'direct' | 'batch';
  isModalOpen: boolean;
  handleModalClose: () => void;
  handleNextClick: () => void;
  // 外部依存関数
  onSubmit: (data: FieldValues) => Promise<void>;
}

function SurveyForm({
  hideSubmit = false,
  isPreview = false,
  customForms,
  lotteryDrawHistoryId,
  lotteryPrizeLabelId,
  wChanceType,
  isModalOpen,
  handleModalClose,
  handleNextClick,
  onSubmit,
}: SurveyFormProps) {
  const [isLoading, setIsLoading] = useState(false);

  // デフォルト値を配列にしてをまとめて返す関数
  const createDefaultValues = (forms: WChanceSurveyFormItem[] = []): FieldValues => {
    const values = forms.reduce((acc, form) => {
      switch (form.type) {
        case 'checkbox':
          acc[form.hash] = form.value ? (Array.isArray(form.value) ? form.value : [form.value]) : [];
          break;
        case 'radio':
          acc[form.hash] = form.value ? String(form.value) : 0;
          break;
        default:
          acc[form.hash] = form.value != null ? String(form.value) : '';
      }
      return acc;
    }, {} as FieldValues);

    return values;
  };

  const { handleSubmit, control } = useForm({
    mode: 'onChange',
    defaultValues: createDefaultValues(customForms),
  });

  const submit = async (data: FieldValues) => {
    if (isLoading) return;
    if (!lotteryDrawHistoryId && !lotteryPrizeLabelId) return;
    setIsLoading(true);
    try {
      await onSubmit(data);
    } catch (error) {
      if (error instanceof Error) {
        await showError([error.message]);
      } else {
        await showError(['必須項目が入力されていません。ご確認の上、再度入力をお願いいたします。']);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // エラー表示関数
  const showError = async (messages: string[]) => {
    await swal.messages({ messages });
  };

  const submitError = () => {
    swal.invalid();
  };

  const customFormItemJsx = (
    customFormItem: WChanceSurveyFormItem,
    control: Control<FieldValues>,
    index: number,
    styles: Record<string, string>,
  ) => {
    const labelWithRequired = (
      <p>
        <span className={styles.label}>
          <span className={styles.questionNumber}>質問{index + 1}.　</span>
          {customFormItem.label}
        </span>
        {customFormItem.required && <span className={styles.required}>*</span>}
      </p>
    );

    switch (customFormItem.type) {
      case 'text':
        return (
          <div className={styles.fullWidth} key={customFormItem.hash}>
            {labelWithRequired}
            <FormText
              required={customFormItem.required}
              name={customFormItem.hash}
              control={control}
              placeholder={customFormItem.placeholder || undefined}
              outlined
            />
          </div>
        );
      case 'number':
        return (
          <div className={styles.fullWidth} key={customFormItem.hash}>
            {labelWithRequired}
            <FormText
              required={customFormItem.required}
              name={customFormItem.hash}
              control={control}
              placeholder={customFormItem.placeholder || undefined}
              type='number'
            />
          </div>
        );
      case 'email':
        return (
          <div className={styles.fullWidth} key={customFormItem.hash}>
            {labelWithRequired}
            <FormText
              required={customFormItem.required}
              name={customFormItem.hash}
              control={control}
              placeholder={customFormItem.placeholder || undefined}
              outlined
              type='email'
            />
          </div>
        );
      case 'tel':
        return (
          <div className={styles.fullWidth} key={customFormItem.hash}>
            {labelWithRequired}
            <FormText
              required={customFormItem.required}
              name={customFormItem.hash}
              control={control}
              placeholder={customFormItem.placeholder || undefined}
              outlined
              type='tel'
            />
          </div>
        );
      case 'textarea':
        return (
          <div className={styles.fullWidth} key={customFormItem.hash}>
            {labelWithRequired}
            <FormTextArea
              required={customFormItem.required}
              name={customFormItem.hash}
              control={control}
              placeholder={customFormItem.placeholder || undefined}
              outlined
            />
          </div>
        );
      case 'url':
        return (
          <div className={styles.fullWidth} key={customFormItem.hash}>
            {labelWithRequired}
            <FormText
              required={customFormItem.required}
              name={customFormItem.hash}
              control={control}
              placeholder={customFormItem.placeholder || undefined}
              outlined
            />
          </div>
        );
      case 'checkbox':
        return (
          <div className={styles.fullWidth} key={customFormItem.hash}>
            {labelWithRequired}
            <FormCheckbox
              required={customFormItem.required}
              name={customFormItem.hash}
              control={control}
              items={
                customFormItem.options?.map((option) => ({
                  value: option.value.toString(),
                  child: <p className={styles.optionLabel}>{option.label}</p>,
                })) || []
              }
            />
          </div>
        );
      case 'radio':
        // biome-ignore lint/correctness/noSwitchDeclarations: <explanation>
        const options =
          customFormItem.options?.map((item) => ({
            value: item.value.toString(),
            text: item.label,
          })) || [];
        return (
          <div className={styles.fullWidth} key={customFormItem.hash}>
            {labelWithRequired}
            <FormSelect
              required={customFormItem.required}
              name={customFormItem.hash}
              control={control}
              items={[{ value: '0', text: '選択してください' }].concat(options)}
            />
          </div>
        );
      default:
        return <div key={customFormItem.hash} />;
    }
  };

  return (
    <>
      {/* フォームタイトル */}
      <div className={`${currentStyles.mtSmall}`}>
        <p className={`${currentStyles.textSmall} ${currentStyles.textBold}`}>{isPreview && 'アンケートプレビュー'}</p>
      </div>

      {/* 入力フォーム */}
      <div className={`${currentStyles.mtSmall} ${layoutStyles.formWrapper}`}>
        {customForms.map((form, index) => (
          <div className={currentStyles.fullWidth} key={form.hash}>
            <span className={currentStyles.required}>{customFormItemJsx(form, control, index, currentStyles)}</span>
          </div>
        ))}
      </div>

      {/* submitボタン （プレビューモードでは非表示） */}
      {!isPreview && !hideSubmit && (
        <div className={`${currentStyles.centeredVertical} ${currentStyles.mtMedium}`}>
          <ActionButton disabled={isLoading} onClick={handleSubmit(submit, submitError)} isLoading={isLoading}>
            入力を完了する
          </ActionButton>
        </div>
      )}
      {/* エントリー完了モーダル */}
      {wChanceType === WCHANCE_TYPE.batch && (
        <ModalBase isOpen={isModalOpen} onClose={handleModalClose}>
          <div className={` ${utilsStyles.padding} ${styles.modalContainer}`}>
            <div className={`${utilsStyles.textCenter} ${utilsStyles.mbMedium}`}>
              <div className={`${styles.wchanceHeader} ${styles.wchanceHeaderText} ${utilsStyles.mtSmall}`}>
                <span className={fontsStyles.montserrat}>W</span>チャンス賞
              </div>
              <p className={`${utilsStyles.textLarge} ${utilsStyles.textBold} ${utilsStyles.mtSmall}`}>
                エントリーが完了しました
              </p>
            </div>
            <div className={`${utilsStyles.centered} ${utilsStyles.mtSmall} `}>
              <ActionButton size='large' color='primary' onClick={handleNextClick}>
                次へ進む
              </ActionButton>
            </div>
          </div>
        </ModalBase>
      )}
    </>
  );
}

export default SurveyForm;
